import { useQuery } from '@tanstack/react-query';

import { readWorkspaceAiModelSpend } from '@/lib/api/generated/api';
import type { WorkspaceAIModelSpend } from '@/lib/api/generated/model';
import { UsageError, isForbidden } from '../errors';
import { usageKeys } from '../queryKeys';

/**
 * A workspace's spend for a period by workflow, AI profile and AI connection, each
 * with the lines under it (spec 225, service spec 172).
 *
 * A read of its own beside the usage summary, not part of it: the service keeps
 * profile-shaped figures out of the usage feature. The same rules as
 * `useWorkspaceUsage` — anything but a 200 throws, so a failed read is never drawn
 * as an empty table, and a refusal is not retried.
 */
export function useWorkspaceSpendGroups(
  workspaceId: string,
  period: string | undefined,
) {
  return useQuery<WorkspaceAIModelSpend>({
    queryKey: usageKeys.spendGroups(workspaceId, period),
    queryFn: async ({ signal }) => {
      const response = await readWorkspaceAiModelSpend(
        workspaceId,
        period === undefined ? {} : { period },
        { signal },
      );
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only this workspace’s owner can see what it is spending.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not read where this workspace’s spend went.',
      );
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
}
